package com.arena_web.ui.sections.news

import androidx.compose.runtime.*
import com.arena_web.data.dto.PlayerDetailDto
import com.arena_web.di.LocalAppContainer
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.components.widgets.SkeletonBox
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img

// Player bio mini-card — sheet on phones, centred popup on desktop. Opened
// from lineup rows, timeline events and the club squad grid.

@Composable
fun PlayerCardOverlay(
    sport: String,
    playerId: String,
    mobile: Boolean,
    onDismiss: () -> Unit,
) {
    val repository = LocalAppContainer.current.newsRepository
    var player by remember(playerId) { mutableStateOf<PlayerDetailDto?>(null) }
    var failed by remember(playerId) { mutableStateOf(false) }
    LaunchedEffect(playerId) {
        val locale = com.arena_web.utils.Storage.get(com.arena_web.utils.StorageKeys.LANGUAGE)
        repository.getPlayer(sport, playerId, locale)
            .onSuccess { player = it }
            .onFailure { failed = true }
    }

    val body: @Composable () -> Unit = { PlayerCardBody(player, failed, mobile) }
    if (mobile) {
        com.arena_web.ui.components.overlay.BottomSheet(onDismiss = onDismiss) { body() }
    } else {
        com.arena_web.ui.components.overlay.DesktopPopup(onDismiss = onDismiss, widthPx = 460) { body() }
    }
}

@Composable
private fun PlayerCardBody(
    player: PlayerDetailDto?,
    failed: Boolean,
    mobile: Boolean,
) {
    val t = LocalAppStrings.current
    if (failed && player == null) {
        SpanText(
            t.newsDetailUnavailable,
            modifier = Modifier.fillMaxWidth().fontSize(13.px)
                .styleModifier { property("text-align", "center"); property("color", "var(--arena-text-3)") }
        )
        return
    }
    val p = player
    if (p == null) {
        // Shimmer placeholders while the bio loads.
        Column(modifier = Modifier.fillMaxWidth().styleModifier { property("gap", "12px") }) {
            Row(modifier = Modifier.styleModifier { property("gap", "14px") }, verticalAlignment = Alignment.CenterVertically) {
                SkeletonBox(width = 72.px, height = 72.px, borderRadius = 100.px)
                Column(modifier = Modifier.styleModifier { property("gap", "8px"); property("flex", "1 1 0") }) {
                    SkeletonBox(width = 60.percent, height = 18.px)
                    SkeletonBox(width = 40.percent, height = 12.px)
                }
            }
            SkeletonBox(height = 60.px, borderRadius = 12.px)
        }
        return
    }

    Column(modifier = Modifier.fillMaxWidth().styleModifier { property("gap", "14px") }) {
        Row(
            modifier = Modifier.fillMaxWidth().styleModifier { property("gap", "14px") },
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(modifier = Modifier.styleModifier { property("position", "relative"); property("flex-shrink", "0") }) {
                if (!p.photo.isNullOrBlank()) {
                    Img(
                        src = p.photo, alt = "",
                        attrs = Modifier.styleModifier {
                            property("width", if (mobile) "76px" else "84px")
                            property("height", if (mobile) "76px" else "84px")
                            property("border-radius", "100px")
                            property("object-fit", "cover")
                            property("object-position", "top")
                            property("background", "radial-gradient(circle at 50% 30%, rgba(60,121,241,0.28), rgba(60,121,241,0.06))")
                            property("border", "0.5px solid rgba(180, 204, 246, 0.22)")
                            property("display", "block")
                        }.toAttrs()
                    )
                } else {
                    Div(attrs = {
                        style {
                            property("width", if (mobile) "76px" else "84px")
                            property("height", if (mobile) "76px" else "84px")
                            property("border-radius", "100px")
                            property("background", "rgba(60, 121, 241, 0.10)")
                        }
                    })
                }
                p.number?.let { number ->
                    Div(attrs = {
                        style {
                            property("position", "absolute")
                            property("right", "-2px")
                            property("bottom", "0")
                            property("min-width", "22px")
                            property("padding", "1px 5px")
                            property("border-radius", "100px")
                            property("background", "var(--l-s3, #0B1B3E)")
                            property("border", "0.5px solid rgba(180, 204, 246, 0.35)")
                            property("text-align", "center")
                        }
                    }) {
                        SpanText(
                            number,
                            modifier = Modifier.fontSize(11.px).fontWeight(700)
                                .styleModifier { property("color", "var(--arena-text-1)") }
                        )
                    }
                }
            }
            Column(modifier = Modifier.styleModifier { property("min-width", "0"); property("gap", "3px") }) {
                SpanText(
                    p.name,
                    modifier = Modifier.fontSize(if (mobile) 18.px else 20.px).fontWeight(700)
                        .styleModifier { property("color", "var(--arena-text-1)") }
                )
                val roleLine = listOfNotNull(p.position, p.team).joinToString(" · ")
                if (roleLine.isNotBlank()) {
                    SpanText(
                        roleLine,
                        modifier = Modifier.fontSize(12.px)
                            .styleModifier { property("color", "var(--arena-text-2)") }
                    )
                }
                // Nationality · age · height · weight — compact fact line.
                val facts = listOfNotNull(
                    p.nationality,
                    p.born?.let { ageLabel(it) },
                    p.height,
                    p.weight,
                ).joinToString(" · ")
                if (facts.isNotBlank()) {
                    SpanText(
                        facts,
                        modifier = Modifier.fontSize(11.px)
                            .styleModifier { property("color", "var(--arena-text-3)"); property("line-height", "1.5") }
                    )
                }
            }
        }

        p.description?.takeIf { it.isNotBlank() }?.let { descr ->
            SpanText(
                descr,
                modifier = Modifier.fontSize(12.px)
                    .styleModifier {
                        property("color", "var(--arena-text-2)")
                        property("line-height", "1.6")
                        property("display", "-webkit-box")
                        property("-webkit-line-clamp", "8")
                        property("-webkit-box-orient", "vertical")
                        property("overflow", "hidden")
                    }
            )
        }
    }
}

/** "27" from a yyyy-MM-dd birth date — rendered inline in the fact line. */
private fun ageLabel(born: String): String? {
    val birth = kotlin.js.Date(born)
    if (birth.getTime().isNaN()) return null
    val now = kotlin.js.Date()
    var age = now.getFullYear() - birth.getFullYear()
    if (now.getMonth() < birth.getMonth() ||
        (now.getMonth() == birth.getMonth() && now.getDate() < birth.getDate())
    ) {
        age--
    }
    return age.takeIf { it in 14..60 }?.toString()
}
